import { useMemo, useState } from 'react'
import { Modal } from './Modal'
import { Thumb } from './Thumb'
import { api, ApiError } from '../lib/api'
import { useToast } from '../lib/toast'
import type { Category, Item, Meal } from '@shared/types'

/** A quick palette of whole-lunch emoji; any other can be left blank. */
const SUGGESTED = [
  '🍱','🥪','🥯','🌯','🍕','🍝','🥗','🧃','🧀','🍎','⭐','🌈','🚀','🦖','🐙','🎒','🏕️','🎂','❄️','☀️',
]

interface Props {
  /** Existing meal to edit, or null to create one. */
  meal: Meal | null
  categories: Category[]
  items: Item[]
  /** Pre-selected components, e.g. when saving a planned day as a meal. */
  initialItemIds?: string[]
  onSaved: (meals: Meal[]) => void
  onClose: () => void
  onError: (err: unknown, fallback: string) => void
}

/**
 * Create or change a saved meal: what it is called, why it works, and which
 * items go in it.
 *
 * Components are picked with a plain `<select>` per category rather than a
 * nested picker modal. It keeps the whole meal visible while it is assembled,
 * it handles a 30-item category natively on a phone, and adding a second side
 * stays possible -- the slot is a list, not a single value.
 */
export function MealEditor({
  meal,
  categories,
  items,
  initialItemIds,
  onSaved,
  onClose,
  onError,
}: Props) {
  const toast = useToast()
  const [name, setName] = useState(meal?.name ?? '')
  const [emoji, setEmoji] = useState(meal?.emoji ?? '')
  const [notes, setNotes] = useState(meal?.notes ?? '')
  const [favorite, setFavorite] = useState(meal?.favorite ?? false)
  const [itemIds, setItemIds] = useState<string[]>(meal?.itemIds ?? initialItemIds ?? [])
  const [busy, setBusy] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items])

  /* Archived items stay selectable only if the meal already has them: they are
     out of the pantry, but silently dropping one from a saved meal would be a
     worse surprise than showing it. */
  const choosable = (categoryId: string) =>
    items.filter(
      (i) => i.categoryId === categoryId && (!i.archived || itemIds.includes(i.id)) && !itemIds.includes(i.id),
    )

  const chosen = (categoryId: string) =>
    itemIds
      .map((id) => itemsById.get(id))
      .filter((i): i is Item => Boolean(i) && i!.categoryId === categoryId)

  const add = (itemId: string) => setItemIds((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]))
  const drop = (itemId: string) => setItemIds((prev) => prev.filter((id) => id !== itemId))

  async function save() {
    const trimmed = name.trim()
    if (!trimmed || itemIds.length === 0 || busy) return
    setBusy(true)
    try {
      const meals = meal
        ? await api.updateMeal(meal.id, { name: trimmed, notes, emoji, favorite, itemIds })
        : (await api.createMeal({ name: trimmed, notes, emoji, favorite, itemIds })).meals
      toast.notify(meal ? `Saved ${trimmed}.` : `${trimmed} is now a saved meal.`)
      onSaved(meals)
      onClose()
    } catch (err) {
      onError(err, err instanceof ApiError ? err.message : 'Could not save that meal.')
      setBusy(false)
    }
  }

  async function remove() {
    if (!meal || busy) return
    setBusy(true)
    try {
      const meals = await api.deleteMeal(meal.id)
      toast.notify(`Deleted ${meal.name}.`)
      onSaved(meals)
      onClose()
    } catch (err) {
      onError(err, 'Could not delete that meal.')
      setBusy(false)
    }
  }

  return (
    <Modal
      title={meal ? 'Edit meal' : 'New saved meal'}
      onClose={onClose}
      footer={
        meal && confirmingDelete ? (
          <>
            <span className="muted" style={{ fontSize: 13 }}>
              Delete this meal? Days already planned with it keep their food.
            </span>
            <div className="spacer" />
            <button className="btn sm" onClick={() => setConfirmingDelete(false)} disabled={busy}>
              Keep
            </button>
            <button className="btn sm danger" onClick={remove} disabled={busy}>
              Delete
            </button>
          </>
        ) : (
          <>
            {meal && (
              <button className="btn sm danger" onClick={() => setConfirmingDelete(true)} disabled={busy}>
                Delete
              </button>
            )}
            <div className="spacer" />
            <button
              className="btn primary"
              onClick={save}
              disabled={busy || !name.trim() || itemIds.length === 0}
            >
              {busy ? 'Saving…' : 'Save meal'}
            </button>
          </>
        )
      }
    >
      <div className="field">
        <label htmlFor="meal-name">Name</label>
        <input
          id="meal-name"
          type="text"
          autoComplete="off"
          value={name}
          placeholder="Bagel day, The usual, Field trip box…"
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      <div className="field">
        <label>What&rsquo;s in it</label>
        {categories.map((category) => {
          const picked = chosen(category.id)
          const available = choosable(category.id)
          return (
            <div className="meal-slot" key={category.id}>
              <div className="meal-slot-head">
                <span className="slot-dot" style={{ background: category.color }} aria-hidden="true" />
                {category.name}
              </div>
              {picked.map((item) => (
                <div className="chip" key={item.id}>
                  <Thumb emoji={item.emoji} imageKey={item.imageKey} name={item.name} />
                  <span className="name">
                    {item.name}
                    {item.archived && <span className="muted"> · archived</span>}
                  </span>
                  <button
                    className="x"
                    onClick={() => drop(item.id)}
                    aria-label={`Remove ${item.name} from this meal`}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <select
                value=""
                aria-label={`Add ${category.name.toLowerCase()} to this meal`}
                disabled={available.length === 0}
                onChange={(e) => {
                  if (e.target.value) add(e.target.value)
                  e.target.value = ''
                }}
              >
                <option value="">
                  {available.length === 0
                    ? `No more ${category.name.toLowerCase()} to add`
                    : picked.length === 0
                      ? `Choose ${category.name.toLowerCase()}…`
                      : `Add another ${category.name.toLowerCase()}…`}
                </option>
                {available.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.emoji ? `${item.emoji} ` : ''}
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          )
        })}
        {itemIds.length === 0 && (
          <p className="muted" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
            A meal needs at least one item. It does not have to fill every slot.
          </p>
        )}
      </div>

      <div className="field">
        <label htmlFor="meal-notes">Why it works (optional)</label>
        <input
          id="meal-notes"
          type="text"
          autoComplete="off"
          value={notes}
          placeholder="Packs flat, survives a hot day, never comes home…"
          maxLength={500}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      <div className="field">
        <label>Emoji</label>
        <div className="emoji-grid">
          {SUGGESTED.map((e) => (
            <button
              key={e}
              type="button"
              className="emoji-btn"
              aria-pressed={emoji === e}
              onClick={() => setEmoji(emoji === e ? '' : e)}
            >
              {e}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <button
          type="button"
          className="btn sm"
          aria-pressed={favorite}
          onClick={() => setFavorite((v) => !v)}
        >
          {favorite ? '★ Favourite' : '☆ Make it a favourite'}
        </button>
        <p className="muted" style={{ fontSize: 12.5, margin: '4px 0 0' }}>
          Favourites sit at the top of the list when you fill a day.
        </p>
      </div>
    </Modal>
  )
}
